import { useCallback, useRef } from "react";
import {
  useMutation as useTanstackMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";

/**
 * Drop-in `useMutation` that ignores overlapping calls on the same instance.
 * `disabled={isPending}` is too late — a second click still fires before re-render.
 */
export function useMutation<TData = unknown, TError = Error, TVariables = void, TOnMutateResult = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  type Result = UseMutationResult<TData, TError, TVariables, TOnMutateResult>;
  // Named up here rather than as `typeof mutation.mutate` inside the callbacks:
  // referencing `mutation` in a callback body — even only in a type position —
  // makes react-hooks/exhaustive-deps demand it as a dependency.
  type MutateOptions = Parameters<Result["mutate"]>[1];
  type MutateAsyncOptions = Parameters<Result["mutateAsync"]>[1];

  const mutation = useTanstackMutation(options);
  const inFlight = useRef(false);
  const shared = useRef<Promise<TData> | null>(null);

  const mutateAsync = useCallback(
    (variables: TVariables, mutateOptions?: MutateAsyncOptions) => {
      if (shared.current) return shared.current;
      inFlight.current = true;
      const pending = mutation.mutateAsync(variables, mutateOptions).finally(() => {
        inFlight.current = false;
        shared.current = null;
      });
      shared.current = pending;
      return pending;
    },
    [mutation],
  );

  const mutate = useCallback(
    (variables: TVariables, mutateOptions?: MutateOptions) => {
      if (inFlight.current) return;
      void mutateAsync(variables, mutateOptions as MutateAsyncOptions).catch(() => undefined);
    },
    [mutateAsync],
  ) as Result["mutate"];

  return { ...mutation, mutate, mutateAsync };
}
